// THE SHORT BRIEFING IN THE RING POPUP (Stage 4.5, the owner's design 2026-10-26): who it is as the CRM knows them, and — when
// the assistant passed the call on — to which department and why, in its own words; then up to four facts (an open order, the
// next appointment, open tickets, when they were last in touch). Read in the seconds a phone rings. The popup is always dark, so
// this is drawn in the dark surface's own tokens (--nav-*), the one dark surface the design system has. `light` (4.8f.4a): the new
// phone's incoming card is a light card (mockup "Incoming") — the same briefing in the light surface's tokens.
import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { Bot, CalendarClock, MessageCircle, Phone, Reply, ShoppingBag, Star, Ticket } from "lucide-react";

import { dayAndTime, relativeTime } from "../../../utils/times";
import { ringFacts } from "./briefingMeta";

const ICON = { order: ShoppingBag, appointment: CalendarClock, ticket: Ticket, call: Phone, whatsapp: MessageCircle, followUp: Reply };
const ui = { fontFamily: "var(--font-ui)" };

const DARK  = { text: "var(--nav-item)", bg: "var(--nav-item-hover-bg)", line: "var(--nav-divider)", accent: "var(--brand-cyan)" };
const LIGHT = { text: "var(--ink-600)", bg: "var(--surface-2)", line: "var(--line-100)", accent: "var(--c-cyan)" };

export default function RingBriefing({ briefing = null, handoff = null, light = false }) {
  const { t } = useTranslation("handoff");
  const P = light ? LIGHT : DARK;
  const c = briefing?.contact ?? null;
  const facts = ringFacts(briefing, { t, dayAndTime, relativeTime });
  const about = [c?.status, ...(c?.tags ?? [])].filter(Boolean);
  if (!c && !handoff && !facts.length) return null;
  return (
    <Box component="section" aria-label={t("briefing.title")} sx={{ mt: "var(--sp-3)", display: "grid", gap: "var(--sp-3)" }}>
      {(about.length > 0 || c?.vip) && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "var(--sp-2)", flexWrap: "wrap" }}>
          {c?.vip && (
            <Box component="span" sx={{ ...ui, display: "inline-flex", alignItems: "center", gap: "4px", color: "var(--warning)", fontSize: "var(--t-caption)", fontWeight: 700 }}>
              <Star size={12} aria-hidden="true" /> {t("briefing.vip")}
            </Box>
          )}
          {about.length > 0 && (
            <Typography component="span" sx={{ ...ui, fontSize: "var(--t-caption)", color: P.text }}>{about.join(" · ")}</Typography>
          )}
        </Box>
      )}

      {handoff && (
        <Box sx={{ textAlign: "start", p: "var(--sp-3)", borderRadius: "var(--r-md)", bgcolor: P.bg, border: `1px solid ${P.line}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "var(--sp-2)", flexWrap: "wrap" }}>
            <Bot size={14} aria-hidden="true" color={P.accent} />
            <Typography component="span" sx={{ ...ui, fontSize: "var(--t-caption)", fontWeight: 700, color: P.accent }}>{t("title")}</Typography>
            {handoff.section && <Typography component="span" sx={{ ...ui, fontSize: "var(--t-caption)", color: P.text }}>· {handoff.section}</Typography>}
          </Box>
          <Typography dir="auto" sx={{ ...ui, mt: "4px", fontSize: "var(--t-body-sm)", color: P.text, overflowWrap: "anywhere" }}>
            {handoff.reason ? `“${handoff.reason}”` : t("noReason")}
          </Typography>
        </Box>
      )}

      {facts.length > 0 && (
        <Box component="ul" sx={{ m: 0, p: 0, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "var(--sp-2)" }}>
          {facts.map((f) => {
            const Icon = ICON[f.kind] ?? CalendarClock;
            return (
              <Box component="li" key={f.key} sx={{ ...ui, listStyle: "none", display: "inline-flex", alignItems: "center", gap: "6px", px: "10px", py: "4px",
                borderRadius: "var(--r-pill)", bgcolor: P.bg, border: `1px solid ${P.line}`, color: P.text, fontSize: "var(--t-caption)" }}>
                <Icon size={12} aria-hidden="true" />
                <span dir="auto">{f.text}</span>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
